import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { baseQueryConfig } from 'redux/api/BaseQueryConfig';
import { ENDPOINTS } from 'config/constant/endpoints.config';
import {
  transformDealPricesToTableResume,
  transformDealsDetails,
  transformDiscountResponse,
  transformDiscountsDetails,
  transformListDealItems,
  transformListDiscountPrices,
  transformListDiscountPricesToTableResume,
} from 'redux/api/deals/dealsApi.decoder';
import {
  IDealPricesList,
  IDealPricesListApi,
  IDetailsDeal,
  IDetailsDealApi,
  IDetailsDiscountsApi,
} from 'types/models/Deals/deals.schema';
import Paginator, { DEFAULT_ITEM_PER_PAGE_DISCOUNT_PRICES } from 'types/interfaces/Paginator';
import {
  injectExpand,
  injectFilterProductCatalog,
  injectFilterStatus,
  injectFilterWarehouseIds,
  paginatorToApiPagination,
} from 'utils/services/api.service';
import { injectParams } from 'utils/helpers/api-url';
import {
  IDiscountPackingPricesList,
  IDiscountPricesForResume,
  IDiscountPricesListApi,
  IDiscountResponse,
  IDiscountResponseApi,
  ISelectedProductForDiscountDeal,
  ISelectedProductForPositioning,
} from 'types/models/Discount/discounts.schema';
import { ExpandEnum, Nillable } from 'types/interfaces/sharedType.type';

import { PackingTypeEnum } from 'types/enum/packing-type.enum';

export const dealsApi = createApi({
  reducerPath: 'dealsApi',
  baseQuery: baseQueryConfig,
  tagTypes: [
    'dealTarget',
    'dealItem',
    'remiseApi',
    'discountPriceApi',
    'targets',
    'deleteDiscountPrice',
    'deleteTargetZoneItem',
    'deleteDealItem',
  ],
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    getDealDetails: builder.query({
      query: (id: string) => ENDPOINTS.DEALS.GET_DETAILS(id),
      transformResponse: (response: IDetailsDealApi): IDetailsDeal => {
        return transformDealsDetails(response);
      },
      providesTags: ['dealTarget'],
    }),
    createDiscountTargets: builder.mutation({
      query: (body) => ({
        url: ENDPOINTS.DEALS.ADD_DISCOUNT_TARGETS,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['remiseApi'],
    }),
    getListDiscount: builder.query({
      query: (params: { paginator?: Paginator; status: boolean | null }) =>
        injectParams(ENDPOINTS.DEALS.GET_DISCOUNT, {
          ...paginatorToApiPagination(params.paginator),
          ...injectFilterStatus(params.status),
        }),
      transformResponse: (response: IDiscountResponseApi): IDiscountResponse => {
        return transformDiscountResponse(response);
      },
      providesTags: ['remiseApi'],
    }),
    getListPositioning: builder.query({
      query: (params: {
        paginator?: Paginator;
        status: boolean | null;
        warehouseIds?: Nillable<number>;
        catalogsIds?: Nillable<number>;
        expand?: Nillable<ExpandEnum[]>;
      }) =>
        injectParams(ENDPOINTS.DEALS.GET_POSITIONING, {
          ...paginatorToApiPagination(params.paginator),
          ...injectFilterStatus(params.status),
          ...injectFilterWarehouseIds(params.warehouseIds),
          ...injectFilterProductCatalog(params.catalogsIds),
          ...injectExpand(params.expand),
        }),
      transformResponse: (response: IDiscountResponseApi): IDiscountResponse => {
        return transformDiscountResponse(response);
      },
      providesTags: ['remiseApi'],
    }),
    deleteDiscount: builder.mutation({
      query: (id: string) => ({
        url: ENDPOINTS.DEALS.DELETE_DISCOUNT(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['remiseApi'],
    }),
    getDiscountDetails: builder.query({
      query: (params: { id: string; onlyTargetsLoad?: boolean }) =>
        params.onlyTargetsLoad
          ? ENDPOINTS.DEALS.GET_DISCOUNT_TARGETS(params.id)
          : ENDPOINTS.DEALS.GET_DISCOUNT_DETAILS(params.id),
      transformResponse: (response: IDetailsDiscountsApi): IDetailsDeal => {
        return transformDiscountsDetails(response);
      },
      providesTags: ['remiseApi', 'targets'],
    }),

    getListDiscountPrice: builder.query({
      query: (params: { discountId: string; paginator?: Paginator; isPositioningMode?: boolean }) =>
        injectParams(ENDPOINTS.DEALS.GET_LIST_DISCOUNT_PRICE(params.discountId), {
          ...paginatorToApiPagination(
            params.paginator
              ? { ...params.paginator, rowsPerPage: DEFAULT_ITEM_PER_PAGE_DISCOUNT_PRICES }
              : undefined,
          ),
        }),
      transformResponse: (
        response: IDiscountPricesListApi,
      ): IDiscountPackingPricesList | undefined => {
        return transformListDiscountPrices(response);
      },
      providesTags: (_result, _error, arg) =>
        arg.isPositioningMode ? ['discountPriceApi'] : ['targets', 'deleteDiscountPrice'],
    }),
    getListDiscountPricesResume: builder.query({
      query: (params: {
        discountId: string;
        isDiscountMode: boolean;
        isPositioningMode: boolean;
        paginator?: Paginator;
      }) =>
        injectParams(ENDPOINTS.DEALS.GET_LIST_DISCOUNT_PRICE(params.discountId), {
          ...paginatorToApiPagination(params.paginator),
          ...{ loadPriceByDiscountTargets: true, includeTotal: true },
        }),
      transformResponse: (
        response: IDiscountPricesListApi,
        _meta,
        arg,
      ): IDiscountPricesForResume | undefined => {
        return transformListDiscountPricesToTableResume(
          response,
          arg.isDiscountMode,
          arg.isPositioningMode,
        );
      },
      providesTags: ['discountPriceApi', 'deleteTargetZoneItem', 'deleteDiscountPrice'],
    }),
    getSelectedProducts: builder.query<
      ISelectedProductForDiscountDeal | ISelectedProductForPositioning,
      { id: string; isDealMode: boolean; isPositioningMode: boolean }
    >({
      query: (params) => ({
        url: params.isDealMode
          ? ENDPOINTS.DEALS.GET_SELECTED_PRODUCTS_DEAL(params.id)
          : ENDPOINTS.DEALS.GET_SELECTED_PRODUCTS_DISCOUNT(params.id),
      }),
      transformResponse: (
        response: ISelectedProductForDiscountDeal | ISelectedProductForPositioning,
        _meta,
        arg,
      ) => {
        if (arg.isPositioningMode) {
          return response as ISelectedProductForPositioning;
        }
        return response as ISelectedProductForDiscountDeal;
      },
      providesTags: ['discountPriceApi', 'dealItem'],
    }),

    postOrUpdateDiscountPrice: builder.mutation({
      query: ({ id, body }) => ({
        url: ENDPOINTS.DEALS.UPDATE_DISCOUNT_PRICE(id),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['discountPriceApi'],
    }),
    postOrUpdateOneDiscountTarget: builder.mutation({
      query: ({ body }) => ({
        url: ENDPOINTS.DEALS.ADD_ONE_DISCOUNT_TARGET,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['targets'],
    }),
    postOrUpdateFinalDiscountTarget: builder.mutation({
      query: ({ id, body }) => ({
        url: ENDPOINTS.DEALS.ADD_FINAL_DISCOUNT_TARGET(id),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.shouldSkipInvalidationTag ? [] : ['targets', 'discountPriceApi'],
    }),
    deleteDiscountTargetItem: builder.mutation({
      query: ({ targetId, discountId }: { targetId: number; discountId: string }) => ({
        url: ENDPOINTS.DEALS.DELETE_DISCOUNT_TARGET_ITEM(String(targetId), String(discountId)),
        method: 'DELETE',
      }),
      invalidatesTags: ['targets', 'deleteTargetZoneItem'],
    }),
    deleteDiscountPrice: builder.mutation({
      query: ({
        discountPackingPriceId,
        packingType,
      }: {
        discountPackingPriceId: number;
        packingType: PackingTypeEnum;
      }) => ({
        url: ENDPOINTS.DEALS.DELETE_DISCOUNT_PRICE,
        method: 'DELETE',
        body: {
          discountPackingPriceId,
          packingType,
        },
      }),
      invalidatesTags: ['discountPriceApi', 'deleteDiscountPrice'],
    }),

    /// //////////////////////////////////////// Deals /////////////////////////////////////////////////////

    createOrUpdateDealZoneConfig: builder.mutation({
      query: ({ body }) => ({
        url: ENDPOINTS.DEALS.UPDATE_DEAL_ZONE_CONFIG,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['dealTarget'],
    }),
    createOrUpdateDealFinalConfig: builder.mutation({
      query: ({ id, body }) => ({
        url: ENDPOINTS.DEALS.UPDATE_FINAL_DEAL_CONFIG(id),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.shouldSkipInvalidationTag ? [] : ['dealTarget', 'dealItem'],
    }),
    deleteDealTargetItem: builder.mutation({
      query: ({ idDeal, targetItemId }: { idDeal: number; targetItemId: number }) => ({
        url: ENDPOINTS.DEALS.DELETE_DEAL_TARGET_ITEM(String(idDeal), String(targetItemId)),
        method: 'DELETE',
      }),
      invalidatesTags: ['dealTarget'],
    }),
    updateDealSimpleField: builder.mutation({
      query: ({ id, body }) => ({
        url: ENDPOINTS.DEALS.UPDATE_SIMPLE_FIELD(id),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, arg) =>
        arg?.shouldSkipInvalidationTag ? [] : ['dealTarget'],
    }),
    paginateDealItems: builder.query({
      query: (params: { dealId: string; paginator?: Paginator }) =>
        injectParams(ENDPOINTS.DEALS.GET_LIST_DEAL_PRICES(params.dealId), {
          ...paginatorToApiPagination(
            params.paginator
              ? { ...params.paginator, rowsPerPage: DEFAULT_ITEM_PER_PAGE_DISCOUNT_PRICES }
              : undefined,
          ),
        }),
      transformResponse: (response: IDealPricesListApi): IDealPricesList | undefined => {
        return transformListDealItems(response);
      },
      providesTags: ['dealTarget', 'deleteDealItem'],
    }),
    createOrUpdateDealPrices: builder.mutation({
      query: ({ dealId, body }) => ({
        url: ENDPOINTS.DEALS.CREATE_OR_UPDATE_DEAL_PRICES(dealId),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['dealItem'],
    }),

    paginateResumeDeal: builder.query({
      query: (params: { dealId: string; stockBundle: number; paginator?: Paginator }) =>
        injectParams(ENDPOINTS.DEALS.GET_LIST_DEAL_PRICES(params.dealId), {
          ...paginatorToApiPagination(params.paginator),
        }),
      transformResponse: (
        response: IDealPricesListApi,
        _meta,
        arg,
      ): IDiscountPricesForResume | undefined => {
        return transformDealPricesToTableResume(response, arg.stockBundle);
      },
      providesTags: ['dealItem'],
    }),

    deleteDealItem: builder.mutation({
      query: ({ idDeal, idDealItem }: { idDeal: string; idDealItem: string }) => ({
        url: ENDPOINTS.DEALS.DELETE_DEAL_ITEM(idDeal, idDealItem),
        method: 'DELETE',
      }),
      invalidatesTags: ['dealItem', 'deleteDealItem'],
    }),
    getDealsTotals: builder.query({
      query: (params: { dealId: string }) => ENDPOINTS.DEALS.GET_DEALS_TOTALS(params.dealId),

      transformResponse: (response: {
        totalBeforeDiscount: number | null;
        totalAfterDiscount: number | null;
      }):
        | {
            totalBeforeDiscount: number | null;
            totalAfterDiscount: number | null;
          }
        | undefined => ({
        totalBeforeDiscount: response.totalBeforeDiscount,
        totalAfterDiscount: response.totalAfterDiscount,
      }),
      providesTags: ['dealItem'],
    }),
  }),
});
export const {
  useGetDealDetailsQuery,
  useGetListDiscountQuery,
  useDeleteDiscountMutation,
  useGetDiscountDetailsQuery,
  useGetListPositioningQuery,
  useCreateDiscountTargetsMutation,
  useGetListDiscountPriceQuery,
  usePostOrUpdateDiscountPriceMutation,
  useGetSelectedProductsQuery,
  useDeleteDiscountPriceMutation,
  useGetListDiscountPricesResumeQuery,
  usePostOrUpdateOneDiscountTargetMutation,
  usePostOrUpdateFinalDiscountTargetMutation,
  useDeleteDiscountTargetItemMutation,
  // Deals
  useCreateOrUpdateDealZoneConfigMutation,
  useCreateOrUpdateDealFinalConfigMutation,
  useDeleteDealTargetItemMutation,
  useUpdateDealSimpleFieldMutation,
  usePaginateDealItemsQuery,
  useCreateOrUpdateDealPricesMutation,
  usePaginateResumeDealQuery,
  useDeleteDealItemMutation,
  useGetDealsTotalsQuery,
} = dealsApi;
